import { Typography, Grid } from '@rbx/ui';
import React, { Fragment, FunctionComponent } from 'react';
import CodeSamples from '@modules/ui/shared/components/CodeSamples';
import StandardMarkdown from '@modules/ui/markdown/components/StandardMarkdown';
import { APIReferenceMember, isMathOperationMember } from '@modules/client';
import { GlobalCharacteristicRowTypes, LibraryCharacteristicRowTypes } from '../../types';
import {
  isGlobalProperty,
  isGlobalFunction,
  isLibraryFunction,
  isLibraryProperty,
} from '../../utils/characteristicsRowUtils';
import LinkableTypeReference from '../../sharedUI/components/LinkableTypeReference';
import ParameterTable from '../../sharedUI/components/ParameterTable';
import ReturnsTable from '../../sharedUI/components/ReturnsTable';
import ReferenceMemberTags from '../../sharedUI/components/ReferenceMemberTags';
import { getVisibleReferenceTags } from '../../sharedUI/utils/tagUtils';
import { getUniqueMemberTitleHashId } from '../../utils/apiReferencePageNavUtils';

interface GlobalOrLibraryCharacteristicsIteratorProps {
  rows: GlobalCharacteristicRowTypes[] | LibraryCharacteristicRowTypes[];
}

interface IteratorRowProps {
  row: GlobalCharacteristicRowTypes | LibraryCharacteristicRowTypes;
  pageData?: { title: string };
  isSameNameAsPrevMember?: boolean;
}

export const IteratorRow: FunctionComponent<IteratorRowProps> = ({
  row,
  pageData,
  isSameNameAsPrevMember,
}) => {
  let type;
  let trimmedName = row.name;
  const { description } = row;
  const id = getUniqueMemberTitleHashId(
    // FIXME: DatatypeCharacteristicRowTypes needs to be replaced by
    // more generalized APIReferenceMember type
    row as unknown as APIReferenceMember,
    !!isSameNameAsPrevMember,
  );
  const visibleTags = getVisibleReferenceTags(row);

  if (isGlobalProperty(row)) {
    type = row.type;
  }
  if (isGlobalFunction(row)) {
    if (Array.isArray(row.returns) && row.returns.length > 0) {
      const [{ type: returnType }] = row.returns;
      type = returnType;
    }
  }
  if (isLibraryFunction(row) || isLibraryProperty(row)) {
    trimmedName = row.name.substring(row.name.indexOf('.') + 1);
  }
  if (pageData) {
    trimmedName = pageData.title;
  }

  return (
    <Grid item XSmall>
      <Grid container direction='column' id={id}>
        <Grid item XSmall>
          <Grid container alignItems='baseline' spacing={1}>
            <Grid item marginBottom={2}>
              <Typography
                variant={pageData ? 'h1' : 'h3'}
                component={pageData ? 'h1' : 'h3'}
                id={id}>
                {trimmedName}
              </Typography>
            </Grid>
            {typeof type !== 'undefined' ? (
              <Grid item>
                <LinkableTypeReference type={type} />
              </Grid>
            ) : null}
          </Grid>
        </Grid>
        {Array.isArray(visibleTags) && visibleTags.length > 0 && (
          <Grid item XSmall marginTop={1.5} marginBottom={2}>
            <ReferenceMemberTags tags={visibleTags} memberName={trimmedName} />
          </Grid>
        )}
        <Grid item XSmall>
          <Typography variant='body1'>
            <StandardMarkdown>{description}</StandardMarkdown>
          </Typography>
        </Grid>
        {isGlobalFunction(row) ? (
          <Fragment>
            <Grid item XSmall marginBottom={2}>
              <ParameterTable parameters={row.parameters} />
            </Grid>
            <Grid item XSmall marginTop={3}>
              <ReturnsTable returnsList={row.returns} />
            </Grid>
          </Fragment>
        ) : null}
        <Grid item XSmall>
          {Array.isArray(row.codeSamples) && row.codeSamples.length > 0 ? (
            <CodeSamples
              codeSamples={row.codeSamples}
              typographyId='global-or-library-code-samples'
            />
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};

const GlobalOrLibraryCharacteristicsIterator: FunctionComponent<
  React.PropsWithChildren<GlobalOrLibraryCharacteristicsIteratorProps>
> = ({ rows }) => {
  return (
    <Fragment>
      {rows.map((row, i) => {
        const prevMember = rows[i - 1] as unknown as APIReferenceMember;
        const isSameNameAsPrevMember =
          isMathOperationMember(row as unknown as APIReferenceMember) ||
          !prevMember ||
          isMathOperationMember(prevMember)
            ? false
            : row.name === prevMember.name;

        return (
          <IteratorRow
            // eslint-disable-next-line react/no-array-index-key
            key={`${row.name}-${i}`}
            row={row}
            isSameNameAsPrevMember={isSameNameAsPrevMember}
          />
        );
      })}
    </Fragment>
  );
};

export default GlobalOrLibraryCharacteristicsIterator;
